import { guidToString } from '@open-pencil/fig/node-change'
import { copyFills, copyStyleRuns } from '@open-pencil/scene-graph/copy'

import { applyOverridePatch, type OverridePatch } from '../patches'
import { getComponentRoot } from '../resolve'
import type { ComponentPropRef, ComponentPropValue, OverrideContext } from '../types'
import { propTextCharacters } from './values'

function applyPatchAndMark(
  ctx: OverrideContext,
  childId: string,
  patch: OverridePatch,
  modified?: Set<string>
): void {
  if (applyOverridePatch(ctx, patch)) modified?.add(childId)
}

function applyVisibleProp(
  ctx: OverrideContext,
  childId: string,
  val: ComponentPropValue,
  modified?: Set<string>
): void {
  if (val.boolValue === undefined) return
  applyPatchAndMark(
    ctx,
    childId,
    { targetId: childId, source: 'component-prop', props: { visible: val.boolValue } },
    modified
  )
}

function applyTextProp(
  ctx: OverrideContext,
  childId: string,
  val: ComponentPropValue,
  modified?: Set<string>
): void {
  const child = ctx.graph.getNode(childId)
  const text = propTextCharacters(val)
  if (text === undefined || child?.type !== 'TEXT') return
  const source = child.componentId ? ctx.graph.getNode(child.componentId) : null
  const props: Parameters<typeof applyPatchAndMark>[2]['props'] = { text }
  if (source?.type === 'TEXT' && source.text === text) {
    props.width = source.width
    props.height = source.height
    props.fills = copyFills(source.fills)
    props.styleRuns = copyStyleRuns(source.styleRuns)
    props.derivedTextGlyphs = source.derivedTextGlyphs
      ? structuredClone(source.derivedTextGlyphs)
      : undefined
  }
  applyPatchAndMark(ctx, childId, { targetId: childId, source: 'component-prop', props }, modified)
}

function applySwapProp(
  ctx: OverrideContext,
  childId: string,
  val: ComponentPropValue,
  modified?: Set<string>
): void {
  const swapId =
    propTextCharacters(val) ?? (val.guidValue ? guidToString(val.guidValue) : undefined)
  const newCompId = swapId ? ctx.guidToNodeId.get(swapId) : undefined
  if (!newCompId) return
  const currentCompId = ctx.graph.getNode(childId)?.componentId
  if (currentCompId && getComponentRoot(ctx, currentCompId) === getComponentRoot(ctx, newCompId)) {
    return
  }
  applyPatchAndMark(
    ctx,
    childId,
    {
      targetId: childId,
      source: 'component-prop',
      swapComponentId: getComponentRoot(ctx, newCompId)
    },
    modified
  )
}

export function applyComponentPropRef(
  ctx: OverrideContext,
  childId: string,
  ref: ComponentPropRef,
  val: ComponentPropValue,
  modified?: Set<string>
): void {
  switch (ref.componentPropNodeField) {
    case 'VISIBLE':
      applyVisibleProp(ctx, childId, val, modified)
      break
    case 'TEXT_DATA':
      applyTextProp(ctx, childId, val, modified)
      break
    case 'OVERRIDDEN_SYMBOL_ID':
      applySwapProp(ctx, childId, val, modified)
      break
  }
}
